import { useQuery } from '@tanstack/react-query'
import { fetchAllOrderPartsLists, fetchBookedServiceKpis } from '$api/evoAPIs'
import { fetchPartKpis } from '$api/client'
import { useTranslation } from 'react-i18next'

function useServiceKPI(service) {
  const { t } = useTranslation()

  const {
    data: partKpis,
    isLoading: isLoadingPartKpis,
    isError: isErrorPartKpis,
  } = useQuery(['partKpis'], fetchPartKpis, {
    enabled:
      service.id === '63dcfb10f81b69c6d3a7c431' ||
      service.id === '63dcfb10f81b69c6d3a7c42f',
    refetchOnWindowFocus: false,
  })

  const {
    data: bookedServiceKpis,
    isLoading: isLoadingBookedServiceKpis,
    isError: isErrorBookedServiceKpis,
  } = useQuery(['bookedServiceKpis'], fetchBookedServiceKpis, {
    enabled: service.id === 'main',
    refetchOnWindowFocus: false,
  })

  // Fetch order parts
  const orderedServicesQuery = useQuery(
    ['orderPartsLists'],
    fetchAllOrderPartsLists,
    {
      enabled: service.id === 'main',
      refetchOnWindowFocus: false,
    },
  )

  // Determine type of service button
  switch (service.id) {
    case '63dcfb10f81b69c6d3a7c431': {
      if (isLoadingPartKpis) return <span>{t('Loading...')}</span>
      if (isErrorPartKpis) return <span>{t('Error occurred')}</span>

      return (
        <div style={{ marginTop: '10px' }}>
          <p style={{ margin: 0 }}>
            {partKpis.parts_with_passed_fc + partKpis.parts_with_failed_fc}{' '}
            {t('marketplace.of')} {partKpis.total_parts}{' '}
            {t('marketplace.parts_checked')}{' '}
          </p>
          <p style={{ margin: 0 }}>
            <svg
              style={{ margin: '-3px 4px' }}
              viewBox="0 0 12 12"
              width="12"
              height="12"
            >
              <circle cx="6" cy="6" r="4" fill="#34C38F" />
            </svg>
            {`${partKpis.parts_with_passed_fc}`}{' '}
            {t('marketplace.parts_checked_successfully')}
          </p>
        </div>
      )
    }
    case 'main': {
      if (isLoadingBookedServiceKpis) return <span>{t('Loading...')}</span>
      if (isErrorBookedServiceKpis) return <span>{t('Error occurred')}</span>

      const kpiData = service.subservices.flatMap(
        (subservice, index, array) => {
          const getPartCount = () => {
            if (subservice.attributeKeys.includes('Generate 3D Model')) {
              return bookedServiceKpis.parts_with_generate_3d_model_booked
            }
            if (subservice.attributeKeys.includes('Apply Texturing')) {
              return bookedServiceKpis.parts_with_apply_texturing_booked
            }
            return bookedServiceKpis.parts_with_apply_branding_booked
          }

          const element = (
            <span key={subservice.id} style={{ margin: '0 5px' }}>
              {t('marketplace.parts', { count: getPartCount() })}
            </span>
          )

          const isLastElement = index === array.length - 1
          // Avoid adding a slash after the last element
          return isLastElement
            ? [element]
            : [
                element,
                <span
                  key={`slash-${subservice.id}`}
                  style={{ margin: '0 5px' }}
                >
                  /
                </span>,
              ]
        },
      )
      return <div style={{ margin: '10px 5px 20px' }}>{kpiData}</div>
    }

    case '63dcfb10f81b69c6d3a7c42f': {
      if (orderedServicesQuery.isLoading) return <span>{t('Loading...')}</span>
      if (orderedServicesQuery.isError)
        return <span>{t('Error occurred')}</span>
      return (
        <div style={{ margin: '10px 5px 20px' }}>
          <p style={{ margin: 0 }}>
            {t('marketplace.total_ordered', {
              count: orderedServicesQuery.data.total,
            })}
          </p>
        </div>
      )
    }

    default:
      return <div style={{ margin: '10px 5px 20px' }}>{t('no_data')}</div>
  }
}

export default useServiceKPI
